import type { VariantProps } from 'class-variance-authority';
import type {
  ElementType,
  HTMLAttributes,
  LabelHTMLAttributes,
  PropsWithChildren,
} from 'react';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';

const typographyVariants = cva('text-foreground', {
  variants: {
    variant: {
      'headline-1': 'text-headline1 lg:text-headline1desktop',
      'headline-2': 'text-headline2 lg:text-headline2desktop',
      'headline-3': 'text-headline3 lg:text-headline3desktop',
      'subtitle-1': 'text-subtitle1 lg:text-subtitle1desktop',
      'subtitle-2': 'text-subtitle2 lg:text-subtitle2desktop',
      'subtitle-3': 'text-subtitle3 lg:text-subtitle3desktop',
      'title-1': 'text-title1 lg:text-title1desktop',
      'title-2': 'text-title2 lg:text-title2desktop',
      'title-3': 'text-title3 lg:text-title3desktop',
      'title-4': 'text-title4 lg:text-title4desktop',
      'title-5': 'text-title5 lg:text-title5desktop',
      'title-6': 'text-title6 lg:text-title6desktop',
      'title-7': 'text-title7 lg:text-title7desktop',
      body: 'text-body lg:text-bodydesktop',
      'body-large': 'text-bodylarge lg:text-bodylargedesktop',
      helper: 'text-helper lg:text-helperdesktop',
      caption: 'lg:text-caption-desktop text-caption',
    },
  },
  defaultVariants: {
    variant: 'body',
  },
});

export const variants = [
  'headline-1',
  'headline-2',
  'headline-3',
  'subtitle-1',
  'subtitle-2',
  'subtitle-3',
  'title-1',
  'title-2',
  'title-3',
  'title-4',
  'title-5',
  'title-6',
  'body',
  'body-large',
  'helper',
  'caption',
] as const satisfies VariantProps<typeof typographyVariants>['variant'][];

export const tags: ElementType[] = [
  'p',
  'span',
  'label',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'li',
] as const;
export type Tag = (typeof tags)[number];

export type TypographyProps = HTMLAttributes<HTMLElement> &
  Pick<LabelHTMLAttributes<HTMLLabelElement>, 'htmlFor'> &
  PropsWithChildren &
  VariantProps<typeof typographyVariants> & {
    as?: Tag;
    asChild?: boolean;
  };

const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    { as: Tag = 'p', asChild = false, children, className, variant, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : Tag;

    return (
      <Comp
        className={cn(
          typographyVariants({
            variant,
          }),
          className
        )}
        {...props}
        ref={ref}
      >
        <Slottable>{children}</Slottable>
      </Comp>
    );
  }
);
Typography.displayName = 'Typography';

export { Typography };
